import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import MenuIcon from '@mui/icons-material/Menu';
import TableRow from '@mui/material/TableRow';
import Link from '@mui/material/Link';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';

import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';
import { useAvoidSuspense, useSkipFirstLoad } from 'lib';

import Query, {
  MenuListRefetchQuery,
} from '__generated__/MenuListRefetchQuery.graphql';
import { MenuList_data$key } from '__generated__/MenuList_data.graphql';

type Props = {
  data: MenuList_data$key;
};

const MenuList: FC<Props> = props => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [data, { refetch, loading }] = useAvoidSuspense<
    MenuListRefetchQuery,
    MenuList_data$key
  >(
    graphql`
      fragment MenuList_data on Query
      @refetchable(queryName: "MenuListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        search: { type: "String" }
      ) {
        menus(first: $first, after: $after, search: $search) {
          edges {
            node {
              id
              displayName
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    props.data,
    Query
  );

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  const { edges, pageInfo } = data.menus ?? {};

  return (
    <Table
      search
      pagination
      pageInfo={pageInfo ?? null}
      icon={<MenuIcon />}
      loading={loading}
      head={
        <TableRow>
          <TableHeadCell name="name">Meniu</TableHeadCell>
        </TableRow>
      }>
      {edges?.map(({ node: { id, displayName } }) => (
        <TableRow key={id}>
          <TableCell>
            <Link href={id}>{displayName}</Link>
          </TableCell>
        </TableRow>
      ))}
    </Table>
  );
};

export default MenuList;
